import { useIntl } from 'react-intl';
import _ from 'lodash';
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useFormik } from 'formik'
import { deleteOne, patchOne } from '../../../../services/common_requests';
import { toast } from 'react-toastify'
import { useEffect, useRef, useState } from 'react';
import { ID, KTSVG, QUERIES } from '../../../../../_dashboard/helpers';
import { useQueryClient } from 'react-query';
import { images } from '../../../hotel-management/hotel-details/core/_models';
import ImageEditModal from '../../../hotel-management/hotel-details/image-edit-modal/ImageEditModal';
import { AddAttributeModal } from './AddAttributeModal';
import { DeleteResponse } from '../../../hotel-management/hotel-details/HotelDetails';
import { ToggleButton } from './ToggleButton';
import { useConfirm } from '../../../../components/ConfirmProvider';
import { useAuth } from '../../../../modules/auth';
import {
  compactTranslations,
  normalizeLang,
  pickAttributeLabel,
  pickContentField,
  pickLocalizedButtonLink,
  upsertTranslationField,
} from '../core/contentLanguageUtils';
import {ImageDisplaySizeFields} from './ImageDisplaySizeFields';

export interface Translation {
    _id: string;
    language: string;
    title?: string;
    subtitle?: string;
    content?: string;
    value?: string;
    label?:string;
    buttonLink?: string;
}

export interface Attribute {
    _id: string;
    type: string;
    key?: string;
    translations: Translation[];
    image?: {
        _id:string,
        url: string;
        seoUrl: string;
    };
    buttonLink?: string;
    displayWidth?: string;
    displayHeight?: string;
}

interface Block {
    _id: string;
    position?: string;
    translations: Translation[];
    attributes: Attribute[];
    pageId:string
}
interface Image {
    _id: string;
    url: string;
    seoUrl?: string;
}

const BlockComponent = ({ block, pageId, selectedLanguage, handleAttributeAdded, handleAttributeDeleted }: {
    block: any; 
    pageId: string; 
    handleAttributeAdded: (updatedBlockId: string, newAttribute: Attribute) => void;
    handleAttributeDeleted: (updatedBlockId: string, deletedAttributeId: string) => void;
    selectedLanguage: string
}) => {
    const intl = useIntl()
    const confirm = useConfirm()
    useEffect(() => {
        formik.setValues({
            ...block,
            attributes: block.attributes.map((attr: any) => ({
                ...attr,
                translations: compactTranslations(attr.translations),
            })),
        });
    }, [block.attributes]);
    const initialValues = {
        ...block,
        attributes: block.attributes.map((attr: any) => ({
            ...attr,
            translations: compactTranslations(attr.translations)
        }))
    };
    const [selectedImage, setSelectedImage] = useState<Image | null>(null);
    const [selectedAttributeId, setSelectedAttributeId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false)
    const handleImageClick = (attribute: Attribute) => {
        if (!attribute.image?._id) {
            return;
        }
        setSelectedImage(attribute.image);
        setSelectedAttributeId(attribute._id);
        setIsModalOpen(true);
    };
    
    
    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedImage(null)
        setSelectedAttributeId(null)
    }
    const { auth } = useAuth();
    const handleUpdateImage = async (updatedImage: {
        url: string;
        seoUrl?: string;
        buttonLink?: string;
        displayWidth?: string;
        displayHeight?: string;
    }) => {
        if (!selectedImage || !selectedImage._id) {
            return;
        }

        const formData = new FormData();
        if (updatedImage.url !== selectedImage.url) {
            const response = await fetch(updatedImage.url);
            const blob = await response.blob();
            const file = new File([blob], 'updated-image.jpg', { type: blob.type });
            formData.append('image', file);
            formData.append('placement', 'page_block');
        }
        if (updatedImage.seoUrl) {
            formData.append('seoUrl', updatedImage.seoUrl);
        }
    
        try {
            let resolvedUrl = selectedImage.url;
            let resolvedSeoUrl = updatedImage.seoUrl || selectedImage.seoUrl;
            if (updatedImage.url !== selectedImage.url || updatedImage.seoUrl) {
                const response = await patchOne(formData, `/images/${selectedImage._id}?placement=page_block`);
                if (response?.payload?.code !== 2021) {
                    toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.BLOCKS_UPDATED' }), {
                        position: 'top-right',
                        autoClose: 4000,
                    });
                    return;
                }
                resolvedUrl = response?.data?.url || updatedImage.url;
                resolvedSeoUrl = updatedImage.seoUrl || selectedImage.seoUrl;
            }

            const currentAttr = formik.values.attributes.find(
                (item: Attribute) => item._id === selectedAttributeId,
            )
            const nextTranslations = upsertTranslationField(
                currentAttr?.translations || [],
                selectedLanguage,
                'buttonLink',
                updatedImage.buttonLink || '',
                pickAttributeLabel(currentAttr?.translations, currentAttr?.key),
            )

            if (selectedAttributeId) {
                const attrData = new FormData();
                attrData.append('displayWidth', updatedImage.displayWidth || '');
                attrData.append('displayHeight', updatedImage.displayHeight || '');
                const currentTranslation =
                    nextTranslations.find(
                        (translation) =>
                            normalizeLang(translation.language) === normalizeLang(selectedLanguage),
                    ) || nextTranslations[nextTranslations.length - 1]
                if (currentTranslation) {
                    const translationId = String(currentTranslation._id || '').trim()
                    if (/^[a-f0-9]{24}$/i.test(translationId)) {
                        attrData.append('translations[0][_id]', translationId)
                    }
                    attrData.append(
                        'translations[0][language]',
                        currentTranslation.language || selectedLanguage,
                    )
                    if (currentTranslation.value) {
                        attrData.append('translations[0][value]', currentTranslation.value)
                    }
                    if (currentTranslation.label) {
                        attrData.append('translations[0][label]', currentTranslation.label)
                    }
                    attrData.append(
                        'translations[0][buttonLink]',
                        currentTranslation.buttonLink || '',
                    )
                }
                await patchOne(attrData, `/attributes/${selectedAttributeId}`);
            }

            queryClient.invalidateQueries([QUERIES.SECTION_LIST, block.pageId]);
            const updatedAttributes = formik.values.attributes.map((attr: Attribute) =>
                attr._id === selectedAttributeId ||
                attr.image?._id === selectedImage._id ||
                attr.image?.url === selectedImage.url
                    ? {
                        ...attr,
                        displayWidth: updatedImage.displayWidth || '',
                        displayHeight: updatedImage.displayHeight || '',
                        translations: nextTranslations,
                        image: {
                            ...attr.image,
                            _id: selectedImage._id,
                            url: resolvedUrl,
                            seoUrl: resolvedSeoUrl,
                        },
                      }
                    : attr
            );
            formik.setFieldValue('attributes', updatedAttributes);
            handleCloseModal();
            toast.success(intl.formatMessage({ id: 'ADMIN.COMMON.SUCCESS.BLOCKS_UPDATED_SUCCESSFULLY' }), {
                position: 'top-right',
                autoClose: 4000,
            });
        } catch (error) {
            toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.BLOCKS_UPDATED' }), {
                position: 'top-right',
                autoClose: 4000,
            });
        }
    };
    
    const [showAddAttributeModal, setShowAddAttributeModal] = useState(false)
    const queryClient = useQueryClient()
    const handleAddAttribute = (newAttribute: Omit<Attribute, '_id'> & { blockId: string }) => {
        if (newAttribute.blockId !== block._id) return;
        const finalAttribute: Attribute = {
            _id: Date.now().toString(),
            ...newAttribute,
            image: newAttribute.type === 'image' ? { 
                _id: newAttribute.image?._id || '',
                url: newAttribute.image?.url || '',
                seoUrl: newAttribute.image?.seoUrl || '' 
            } : undefined,
            //translations: newAttribute.translations
        };
        formik.setFieldValue('attributes', [...formik.values.attributes, finalAttribute]);

        queryClient.invalidateQueries([QUERIES.SECTION_LIST, block.pageId]);
        handleAttributeAdded(block._id, finalAttribute);
    };
    

    const initialValuesRef = useRef(initialValues);
    const handleDeleteAttribute = async (attributeId: string) => {
        try {
            if (!attributeId) {
                toast.error(intl.formatMessage({ id: 'ERROR.MISSING_ATTRIBUTE_ID' }));
                return;
            }

            if ((attributeId)) {
               const ok = await confirm({ message: intl.formatMessage({ id: 'SECTION.ATTRIBUTE.DELETE_CONFIRM' }) });
               if (!ok) return;
               const response= await deleteOne(attributeId as unknown as ID, '/attributes');
               if (response?.payload?.code === 2020) {
                queryClient.invalidateQueries([QUERIES.SECTION_LIST, block.pageId]);
                
                const updatedAttributes = formik.values.attributes.filter(
                    (attr: Attribute) => attr._id.toString() !== attributeId.toString()
                );
                formik.setFieldValue('attributes', updatedAttributes);
                handleAttributeDeleted(block._id, attributeId);

                toast.success(
                    intl.formatMessage({
                      id: 'ADMIN.COMMON.SUCCESS.ATTRIBUTE_DELETED_SUCCESSFULLY',
                    }),
                    {
                      position: 'top-right',
                      autoClose: 4000,
                    }
                  )
               }

               else {
                toast.error(
                  intl.formatMessage({
                    id: 'ADMIN.COMMON.ERROR.ATTRIBUTE_DELETED',
                  }),
                  {
                    position: 'top-right',
                    autoClose: 4000,
                  }
                )
              }            }

        } catch (error) {
            // Rollback en cas d'erreur
            formik.setFieldValue('attributes', formik.values.attributes);
            toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.DELETE_FAILED',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
        }
    };
    
    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        onSubmit: async (values) => {
            try {
                // Comparer avec les valeurs initiales
                const changedValues: Partial<Block> = _.omitBy(values, (value, key) =>
                    _.isEqual(value, initialValuesRef.current[key as keyof Block])
                );

                // Comparer les attributs individuellement
                changedValues.attributes = values.attributes.filter(
                    (attr: Attribute, index: number) =>
                        !_.isEqual(attr, initialValuesRef.current.attributes[index])
                );

                if (_.isEmpty(changedValues)) {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.NO_MODIFICATION_DETECTED',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                    return;
                }

                const formData = new FormData();
                if (changedValues.position) {
                    formData.append("blockData[position]", changedValues.position);
                }

                if (changedValues.translations) {
                    changedValues.translations.forEach((translation: Translation, index: number) => {
                        if (translation._id) {
                            formData.append(`blockData[translations][${index}][_id]`, translation._id);
                        }
                        formData.append(`blockData[translations][${index}][language]`, translation.language);
                        formData.append(`blockData[translations][${index}][title]`, translation.title || "");
                        formData.append(`blockData[translations][${index}][subtitle]`, translation.subtitle || "");
                        formData.append(`blockData[translations][${index}][content]`, translation.content || "");
                    });
                }

                if (changedValues.attributes) {
                    changedValues.attributes.forEach((attribute: Attribute, index: number) => {
                        formData.append(`attributesData[${index}][_id]`, String(attribute._id));

                        formData.append(`attributesData[${index}][type]`, attribute.type);
                        const translations = compactTranslations(attribute.translations).filter(
                            (translation: Translation) => Boolean(translation.language),
                        );
                        const translationsToSave =
                            attribute.type === 'image'
                                ? translations.filter(
                                      (translation: Translation) =>
                                          normalizeLang(translation.language) ===
                                          normalizeLang(selectedLanguage),
                                  )
                                : translations;
                        const payloadTranslations =
                            attribute.type === 'image' && translationsToSave.length === 0
                                ? [
                                      {
                                          language: selectedLanguage,
                                          label: pickAttributeLabel(translations, attribute.key),
                                          value: '',
                                          buttonLink: pickLocalizedButtonLink(
                                              translations,
                                              selectedLanguage,
                                          ),
                                      } as Translation,
                                  ]
                                : translationsToSave;
                        payloadTranslations.forEach((translation: Translation, tIndex: number) => {
                            const translationId = String(translation._id || '').trim();
                            if (/^[a-f0-9]{24}$/i.test(translationId)) {
                                formData.append(
                                    `attributesData[${index}][translations][${tIndex}][_id]`,
                                    translationId,
                                );
                            }
                            formData.append(
                                `attributesData[${index}][translations][${tIndex}][language]`,
                                translation.language
                            );
                            if (translation.value) {
                                formData.append(
                                    `attributesData[${index}][translations][${tIndex}][value]`,
                                    translation.value
                                );
                            }
                            if (translation.label) {
                                formData.append(
                                    `attributesData[${index}][translations][${tIndex}][label]`,
                                    translation.label
                                );
                            }
                            formData.append(
                                `attributesData[${index}][translations][${tIndex}][buttonLink]`,
                                translation.buttonLink || ""
                            );
                        });
                    if (attribute.type === 'button') {
                        formData.append(
                            `attributesData[${index}][buttonLink]`,
                            attribute.buttonLink || '',
                        );
                    }
                    if (attribute.type === 'image') {
                        formData.append(
                            `attributesData[${index}][displayWidth]`,
                            attribute.displayWidth || '',
                        );
                        formData.append(
                            `attributesData[${index}][displayHeight]`,
                            attribute.displayHeight || '',
                        );
                    }
                    });
                }

                const response = await patchOne(formData, `/blocks/${values._id}/update-with-attributes`);

                if (response?.payload?.code === 2021) {
                    toast.success(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.SUCCESS.SECTION_UPDATED_SUCCESSFULLY',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                    queryClient.invalidateQueries([QUERIES.SECTION_LIST, values.pageId])

                }
                else if (response?.payload?.code === 4003) {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.POSITION_ALREADY_EXIST',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                }
                else if (response?.payload?.code === 4008) {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.INVALID_LANGUAGE_CODE',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                }
                else {
                    toast.error(
                        intl.formatMessage({
                            id: 'ADMIN.COMMON.ERROR.SECTION_UPDATED',
                        }),
                        {
                            position: 'top-right',
                            autoClose: 4000,
                        }
                    )
                }
            } catch (error) {
                toast.error(
                    intl.formatMessage({
                        id: 'ADMIN.COMMON.ERROR.SECTION_UPDATED',
                    }),
                    {
                        position: 'top-right',
                        autoClose: 4000,
                    }
                )
            }
        },
    });
    useEffect(() => {
        formik.setValues({
            ...block,
            attributes: block.attributes.map((attr: any) => ({
                ...attr,
                translations: compactTranslations(attr.translations)
            }))
        });
    }, [block]); 
    
    const renderEditableAttribute = (attribute: any, index: number) => {
        const attrTranslations = formik.values.attributes[index]?.translations ?? attribute.translations ?? []
        const attrLabel = pickAttributeLabel(attrTranslations, attribute.key)
        const attrValue = pickContentField(attrTranslations, selectedLanguage, 'value')
        const setAttrValue = (value: string) => {
            formik.setFieldValue(
                `attributes[${index}].translations`,
                upsertTranslationField(attrTranslations, selectedLanguage, 'value', value, attrLabel),
            )
        }
        switch (attribute.type) {
            case 'image':
                return (
                    <div className="col mb-4">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <h4 className="m-0">
                                {attrLabel}
                            </h4>
                        </div>
                        <section className=''>
                            <div className=''>
                                {attribute.image?.url && (
                                    <div className='d-flex flex-row align-items-top gap-4'>
                                        <img
                                            src={
                                                attribute.image?.url.includes('/images/')
                                                  ? process.env.REACT_APP_API_URL_IMAGE + attribute.image?.url
                                                  : attribute.image?.url
                                            }
                                            alt={attribute.image?.seoUrl}
                                            className='border-image h-175px cursor-pointer'
                                            onClick={() => handleImageClick(attribute)}
                                        />
                                        <div className='d-flex flex-column gap-2 mt-2'>
                                            <span className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                                                onClick={() => handleImageClick(attribute)}>
                                                <i className='bi bi-pencil-fill fs-7'></i>
                                            </span>
                                            <span 
                                                className={`btn btn-icon btn-sm ${auth?.role === 'ROLE_ADMIN' ? 'btn-disabled' : 'btn-active-color-danger'}`}
                                                onClick={() => auth?.role !== 'ROLE_ADMIN' && handleDeleteAttribute(attribute._id)}
                                                style={{ cursor: auth?.role === 'ROLE_ADMIN' ? 'not-allowed' : 'pointer' }}
                                            >
                                                <i className='bi bi-x fs-2'></i>
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <div className='mt-3' style={{ maxWidth: 420 }}>
                                    <label className='form-label'>
                                        {intl.formatMessage({ id: 'BLOCK.BUTTON_LINK' })}
                                    </label>
                                    <input
                                        type='text'
                                        className='form-control'
                                        value={pickLocalizedButtonLink(attrTranslations, selectedLanguage)}
                                        onChange={(event) => {
                                            formik.setFieldValue(
                                                `attributes[${index}].translations`,
                                                upsertTranslationField(
                                                    attrTranslations,
                                                    selectedLanguage,
                                                    'buttonLink',
                                                    event.target.value,
                                                    attrLabel,
                                                ),
                                            )
                                        }}
                                        placeholder='https://…'
                                    />
                                    <div className='form-text text-muted'>
                                        {intl.formatMessage({id: 'BLOCK.BUTTON_LINK_LANG_HINT'})}
                                    </div>
                                    <ImageDisplaySizeFields
                                        width={formik.values.attributes[index]?.displayWidth}
                                        height={formik.values.attributes[index]?.displayHeight}
                                        onChange={({width, height}) => {
                                            formik.setFieldValue(`attributes[${index}].displayWidth`, width)
                                            formik.setFieldValue(`attributes[${index}].displayHeight`, height)
                                        }}
                                    />
                                </div>
                            </div>
                        </section>
                        <ImageEditModal
                            isOpen={isModalOpen}
                            onClose={handleCloseModal}
                            selectedImage={selectedImage || { id:'',url: '', seoUrl: '' }}
                            onSave={handleUpdateImage}
                            placement='page_block'
                            showAttributeFields
                            buttonLink={pickLocalizedButtonLink(
                              formik.values.attributes.find((item: Attribute) => item._id === selectedAttributeId)
                                ?.translations || [],
                              selectedLanguage,
                            )}
                            displayWidth={
                              formik.values.attributes.find((item: Attribute) => item._id === selectedAttributeId)
                                ?.displayWidth || ''
                            }
                            displayHeight={
                              formik.values.attributes.find((item: Attribute) => item._id === selectedAttributeId)
                                ?.displayHeight || ''
                            }
                        />
                    </div>
                )

            case 'text':
                return (
                    <div className="col-md-4 mb-3 position-relative">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <label className='me-3'>
                                <strong className='form-label'>
                                    {attrLabel}
                                </strong>
                            </label>
                            <span 
                                className={`btn btn-icon btn-sm ${auth?.role === 'ROLE_ADMIN' ? 'btn-disabled' : 'btn-active-color-danger'}`}
                                onClick={() => auth?.role !== 'ROLE_ADMIN' && handleDeleteAttribute(attribute._id)}
                                style={{ cursor: auth?.role === 'ROLE_ADMIN' ? 'not-allowed' : 'pointer' }}
                            >
                                <KTSVG path="/media/icons/duotune/general/gen034.svg" className="svg-icon-2" />
                            </span>
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            value={attrValue}
                            onChange={(e) => setAttrValue(e.target.value)}
                        />
                    </div>
                )

            case 'button':
                return (
                    <div className="mb-3 position-relative">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <label className='me-3'>
                                <strong className='form-label'>
                                    {attrLabel}
                                </strong>
                            </label>
                            <span 
                                className={`btn btn-icon btn-sm ${auth?.role === 'ROLE_ADMIN' ? 'btn-disabled' : 'btn-active-color-danger'}`}
                                onClick={() => auth?.role !== 'ROLE_ADMIN' && handleDeleteAttribute(attribute._id)}
                                style={{ cursor: auth?.role === 'ROLE_ADMIN' ? 'not-allowed' : 'pointer' }}
                            >
                                <KTSVG path="/media/icons/duotune/general/gen034.svg" className="svg-icon-2" />
                            </span>
                        </div>
                        <input
                            type="text"
                            className="form-control"
                            value={attrValue}
                            onChange={(e) => setAttrValue(e.target.value)}
                        />
                        <div className="mt-4">
                            <label className="form-label mb-4">{intl.formatMessage({ id: 'BLOCK.BUTTON_LINK' })}</label>
                            <input
                                type="text"
                                className="form-control"
                                name={`attributes[${index}].buttonLink`}
                                value={formik.values.attributes[index].buttonLink || ''}
                                onChange={formik.handleChange}
                                placeholder={intl.formatMessage({ id: 'BLOCK.ENTER_BUTTON_LINK' })}

                            />
                        </div>

                    </div>
                )

            case 'long-text':
                return (
                    <div className="mb-4 position-relative">
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <label className='me-3'>
                                <strong className='form-label'>
                                    {attrLabel}
                                </strong>
                            </label>
                            <span 
                                className={`btn btn-icon btn-sm ${auth?.role === 'ROLE_ADMIN' ? 'btn-disabled' : 'btn-active-color-danger'}`}
                                onClick={() => auth?.role !== 'ROLE_ADMIN' && handleDeleteAttribute(attribute._id)}
                                style={{ cursor: auth?.role === 'ROLE_ADMIN' ? 'not-allowed' : 'pointer' }}
                            >
                                <KTSVG path="/media/icons/duotune/general/gen034.svg" className="svg-icon-2" />
                            </span>
                        </div>
                        <ReactQuill
                            theme="snow"
                            value={attrValue}
                            onChange={setAttrValue}
                            className="bg-white"
                        />
                    </div>
                )

            default:
                return (
                    <div className="text-danger">
                        Type d'attribut non supporté : {attribute.type}
                    </div>
                )
        }
        
    }

    const handleToggleChange = async (id: string,newIsActive: boolean) => {
        try {
            const payload={
                'isActive':newIsActive
            }
           const response= await patchOne(payload, `/blocks/${block._id}`);
           if (response?.payload?.code === 2021) {
            toast.success(
                intl.formatMessage({
                    id: 'ADMIN.COMMON.SUCCESS.STATUS_UPDATED_SUCCESSFULLY',
                }),
                {
                    position: 'top-right',
                    autoClose: 4000,
                }
            )
            formik.setFieldValue('isActive', newIsActive);


        }
        } catch (error) {
            toast.error(
                intl.formatMessage({
                    id: 'ADMIN.COMMON.ERROR.STATUS_UPDATED',
                }),
                {
                    position: 'top-right',
                    autoClose: 4000,
                }
            )
            formik.setFieldValue('isActive', !newIsActive);
        }
    };

    const blockTranslations = formik.values.translations ?? block.translations ?? []
    const blockTitle = pickContentField(blockTranslations, selectedLanguage, 'title')
    const setBlockTitle = (title: string) => {
        formik.setFieldValue(
            'translations',
            upsertTranslationField(blockTranslations, selectedLanguage, 'title', title),
        )
    }

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div className="card mb-6">
                    <div className="">

                        <h2 className='my-2'>{blockTitle}</h2>
                        <div className='my-3'>
                        <ToggleButton 
                            id={block._id}
                            isActive={formik.values.isActive} 
                            onChange={handleToggleChange} 
                        />
                        </div>
                        <label className="form-label">{intl.formatMessage({ id: 'BLOCK.TITLE' })}</label>
                        <input
                            type="text"
                            className="form-control mb-4"
                            value={blockTitle}
                            onChange={(e) => setBlockTitle(e.target.value)}
                            placeholder={intl.formatMessage({ id: 'BLOCK.TITLE' })}
                        />
                    {/*
                    <div className="mb-4">
                            <label className="form-label">{intl.formatMessage({ id: 'BLOCK.POSITION' })}</label>
                            <input
                                type="number"
                                className="form-control"
                                name="position"
                                value={formik.values.position}
                                onChange={formik.handleChange}
                                min="1"
                            />
                        </div> */} 
                        <div className="row">
                            {formik.values.attributes.map((attribute: any, index: number) => (
                                attribute.type === 'image' ? renderEditableAttribute(attribute, index) : null
                            ))}
                        </div>
                        <div className="row">
                            {formik.values.attributes.map((attribute: any, index: number) => (
                                attribute.type === 'text' ? renderEditableAttribute(attribute, index) : null
                            ))}
                        </div>
                        {formik.values.attributes.map((attribute: any, index: number) => (
                            attribute.type !== 'image' && attribute.type !== 'text' ? renderEditableAttribute(attribute, index) : null
                        ))}
                        
                        <button
                            type="button"
                            className="btn btn-border mt-3"
                            onClick={() => setShowAddAttributeModal(true)}
                            disabled={auth?.role !== 'ROLE_SUPER_ADMIN'}
                        >
                            <KTSVG path='/media/icons/duotune/arrows/addicon.svg' className='svg-icon-2' />
                            {intl.formatMessage({ id: 'ADMIN.ATTRIBUTE.ADD' })}
                        </button>
                        <AddAttributeModal
                            show={showAddAttributeModal}
                            onHide={() => setShowAddAttributeModal(false)}
                            onAdd={handleAddAttribute}
                            blockId={block._id}
                            pageId={pageId}
                            pageLanguage={selectedLanguage}
                            initialValues={{ 
                                type: 'text', 
                                translations: [{
                                    _id:"1",
                                    language: 'fr',
                                    value: '',
                                    label:''
                                  
                                }] 
                            }}
                        />
                        <div className='modal-footer'>
                            <button
                                type="submit"
                                className="btn btn-primary me-2"
                                disabled={formik.isSubmitting}
                            >
                                {intl.formatMessage({
                                    id: 'HEADER.SAVE',
                                })}
                            </button>

                        </div>
                        <div className='separator mt-4'></div>
                    </div>
                </div>
            </form>

        </>


    )
}

export { BlockComponent };